/**
 * Table sorting — and the honest label that has to travel with it.
 *
 * ===========================================================================
 * RECORDED DIVERGENCE: THE API HAS NO `?sort=`, SO SORTING IS PAGE-SCOPED
 * ===========================================================================
 * Figma's `HeaderRow` shows RECEIVED with a sort affordance — the label in
 * `text/primary` and an `accent/indigo` ↓ (node I12:1195;6:58-59) — while the other
 * seven headers are plain `text/muted` labels. So the design authors exactly ONE
 * sorted column, in exactly the order the API already returns:
 * `.orderBy('i.created_at','DESC').addOrderBy('i.id','DESC')`
 * (`inquiries.service.ts`).
 *
 * Part A then states, in the same method, that `?sort=` "is spec §10's general list
 * contract but is not in Part A's inquiry filters; a sort whitelist is deferred rather
 * than invented." The endpoint is closed and B4 may not change it, so there are three
 * possible builds and only one of them is honest:
 *
 *  (a) send `?sort=` anyway — the server ignores it, the arrow moves, the rows do not.
 *      A UI that lies about what it did.
 *  (b) offer no sorting — contradicts the brief AND the design's own affordance.
 *  (c) sort the rows the client HAS, and say so when that is not the whole result set.
 *
 * (c) is implemented. The default sort is byte-for-byte the server's own order, so on
 * first paint nothing is re-ordered and the arrow tells the truth for free. The moment
 * the operator picks anything else, {@link sortScope} returns `'page'` whenever
 * `total > rows.length`, and the table renders a chip saying the sort covers this page
 * only. A coordinator can then choose 100 per page and get a complete sort, which is a
 * real answer rather than a hidden caveat.
 *
 * When `?sort=` lands server-side this file loses `sortRows` and gains a parameter in
 * `listParamsFor`. Nothing else changes; that is why the comparators are pure.
 */

import { INQUIRY_STAGES } from '../../domain/stages';
import type { InquiryDto } from '../../lib/inquiries-api';

/**
 * Sortable columns, keyed by the design's header labels.
 *
 * ⚠ RULE 5 (IQ-9 / D19) — `modules` USED TO BE ABSENT BECAUSE THE COLUMN "IS NOT BUILT:
 * `inquiries` has no modules column and nothing in Phase 2 supplies the tags Figma renders
 * there". That reason is GONE: migration 48 adds `inquiry_platforms` over the shared
 * platform vocabulary (owner ruling 5), `InquiryDto.platforms` carries every row's values
 * by name, and `InquiryTable` now DRAWS the MODULES column. So the refusal became
 * removable, and this is the unit SAYING WHICH way it went:
 *
 *   ⚠ MODULES STAYS UNSORTED, FOR A DIFFERENT REASON THAN BEFORE. It is a SET-valued
 *   column, and a set has no single order to sort by. Every candidate is a claim the
 *   header does not make: by FIRST name puts "Invoice · Report" above "Report" and calls
 *   that ascending; by COUNT sorts a row carrying one value below a row carrying three
 *   and calls that "modules"; by joined text depends on the vocabulary's order, which the
 *   owner can change without a release. Each is a rule an operator would have to be told,
 *   and the arrow cannot tell them. This file's header refuses at (a) a UI that lies about
 *   what it did; a sort whose meaning is not the label's is the quieter version of the
 *   same lie. `KEYS` and `SORT_COLUMNS` are therefore unchanged, and the header cell is a
 *   plain `<th>` with no button and no `aria-sort` — a screen reader is not offered a sort
 *   the table will not perform. If a filter by value is ever wanted, that is
 *   `?platform=` on the list (the CRF register already has one), not a sort.
 *
 * ⚠ `quoted` IS ABSENT FOR A DIFFERENT REASON AGAIN, AND THE DIFFERENCE IS WORTH KEEPING
 * STRAIGHT. `modules` is a column the schema now HAS (through a join) and this file
 * declines to order. `quoted` is a column the schema HAD and no longer does: it sorted
 * `inquiries.expected_value`, dropped by migration 44 (owner ruling Q6). A sortable key
 * over a field the API stopped returning would sort every row into the `missing` bucket
 * and quietly do nothing while the arrow moved — exactly the "UI that lies about what it
 * did" this file's header refuses at (a). That refusal stays as it was.
 */
export const SORT_COLUMNS = ['customer', 'stage', 'received', 'days', 'owner'] as const;
export type SortColumn = (typeof SORT_COLUMNS)[number];
export type SortDir = 'asc' | 'desc';

/** The API's own `ORDER BY`. The table opens here so the arrow starts out truthful. */
export const DEFAULT_SORT: { readonly column: SortColumn; readonly dir: SortDir } = {
  column: 'received',
  dir: 'desc',
};

export function isSortColumn(value: unknown): value is SortColumn {
  return typeof value === 'string' && (SORT_COLUMNS as readonly string[]).includes(value);
}

export function isSortDir(value: unknown): value is SortDir {
  return value === 'asc' || value === 'desc';
}

/** Is the requested order the one the server already returned? */
export function isServerOrder(column: SortColumn, dir: SortDir): boolean {
  return column === DEFAULT_SORT.column && dir === DEFAULT_SORT.dir;
}

export type SortScope =
  /** Every row that matches the filters is on screen — the sort is complete. */
  | 'complete'
  /** The server ordered these rows; nothing was re-ordered here. */
  | 'server'
  /** More rows match than are loaded: this sort covers the current page only. */
  | 'page';

export function sortScope(
  column: SortColumn,
  dir: SortDir,
  loaded: number,
  total: number,
): SortScope {
  if (isServerOrder(column, dir)) return 'server';
  return total > loaded ? 'page' : 'complete';
}

/* ─────────────────────────────────────────────────────────────── the keys ── */

/**
 * One row's value for one column, in the only three shapes a comparison needs.
 *
 * `missing: true` is the load-bearing part. Unknowns — a row with no owner, an amount
 * this caller may not see, a `stage_entered_at` that did not parse — sort LAST in BOTH
 * directions, so flipping the arrow never drags a block of blank cells to the top. A
 * row with no owner is not "the smallest owner", and treating it as one is how a
 * sortable table starts feeling broken.
 */
interface SortKey {
  missing: boolean;
  text: string | null;
  num: number | null;
}

const KNOWN_NONE: SortKey = { missing: true, text: null, num: null };

function textKey(value: string | null): SortKey {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? KNOWN_NONE : { missing: false, text: trimmed, num: null };
}

function numberKey(value: number | null): SortKey {
  return value === null ? KNOWN_NONE : { missing: false, text: null, num: value };
}

/*
 * ⚠ `amountOf` STOOD HERE AND GOES WITH THE `quoted` COLUMN. It read
 * `expected_value` as a number, returning `null` for a MASKED value — which was the
 * whole reason it existed: `Number('•••')` is `NaN`, `NaN - NaN` is `NaN`, and a
 * comparator that returns `NaN` gives an implementation-defined order, i.e. a table that
 * reshuffles on every click.
 *
 * ⚠ THAT HAZARD IS NOT SOLVED, IT IS ABSENT: no surviving sort key on this table reads a
 * maskable field. `customer` and `owner` are names, `stage` is an ENUM rank, `received`
 * and `days` are timestamps. The day a maskable column is sorted here again, this
 * paragraph is the record that `KNOWN_NONE` — not `NaN` — is the answer.
 */

function timeOf(iso: string | null): number | null {
  if (iso === null || iso === '') return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Pipeline order, from the ENUM's DDL order — not alphabetical. */
const STAGE_RANK: Readonly<Record<string, number>> = Object.fromEntries(
  INQUIRY_STAGES.map((stage, index) => [stage, index]),
);

const KEYS: Readonly<Record<SortColumn, (row: InquiryDto) => SortKey>> = {
  customer: (row) => textKey(row.party_name),
  stage: (row) => numberKey(STAGE_RANK[row.stage] ?? null),
  received: (row) => numberKey(timeOf(row.created_at)),
  /**
   * DAYS IN STAGE is derived from `stage_entered_at`, and the two run OPPOSITE ways:
   * an OLDER timestamp is MORE days. The sign is negated here — once, in the key —
   * rather than in the header, so the column sorts by what its label says.
   */
  days: (row) => {
    const ms = timeOf(row.stage_entered_at);
    return numberKey(ms === null ? null : -ms);
  },
  owner: (row) => textKey(row.assigned_to_name),
};

function compareKeys(a: SortKey, b: SortKey): number {
  if (a.num !== null && b.num !== null) return a.num - b.num;
  return (a.text ?? '').localeCompare(b.text ?? '', undefined, { sensitivity: 'base' });
}

/**
 * Sort a page of rows. Returns the input array untouched when the requested order is
 * the server's own — no copy, no work, and provably no re-ordering.
 *
 * The tiebreaker is `id` DESC, matching `addOrderBy('i.id','DESC')`, so equal keys keep
 * the server's relative order rather than depending on `Array.sort` stability.
 */
export function sortRows(
  rows: readonly InquiryDto[],
  column: SortColumn,
  dir: SortDir,
): readonly InquiryDto[] {
  if (isServerOrder(column, dir)) return rows;
  const keyOf = KEYS[column];
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = keyOf(a);
    const right = keyOf(b);
    if (left.missing || right.missing) {
      if (left.missing && right.missing) return compareIds(a.id, b.id);
      return left.missing ? 1 : -1;
    }
    const primary = compareKeys(left, right);
    if (primary !== 0) return primary * factor;
    return compareIds(a.id, b.id);
  });
}

/**
 * `id` DESC over `BIGINT UNSIGNED` decimal strings.
 *
 * Compared as strings, not numbers: ids run to 2^64-1 and `Number()` loses precision
 * above 2^53, which would make two different ids compare equal and reintroduce the
 * unstable order this tiebreaker exists to prevent.
 */
function compareIds(a: string, b: string): number {
  if (a.length !== b.length) return b.length - a.length;
  return b.localeCompare(a);
}

/**
 * Clicking a header: the same column flips the arrow, a new column starts descending.
 *
 * ===========================================================================
 * THE RETURN SHAPE IS THE URL'S SHAPE, AND THAT IS THE POINT
 * ===========================================================================
 * This returned `{column, dir}` once. `InquiryQueryState` spells the same field `sort`, so
 * `update(nextSort(…))` handed the router a patch with an unknown `column` key and a `dir` that
 * was already the default — the arrow moved, the URL did not, and the table stayed in server
 * order. It type-checked, because TypeScript's excess-property check does not apply to a
 * non-literal expression assigned to `Partial<…>`.
 *
 * It was caught by looking at a screenshot, not by a test, so the fix is structural rather than a
 * corrected call site: the function now returns `{sort, dir}` — exactly the two keys the query
 * state holds — and `sort-patch` is asserted in `sorting.test.ts`. A future rename of the state
 * field will now fail to compile here instead of silently doing nothing.
 */
export function sortPatch(
  current: { sort: SortColumn; dir: SortDir },
  clicked: SortColumn,
): { sort: SortColumn; dir: SortDir } {
  if (current.sort !== clicked) return { sort: clicked, dir: 'desc' };
  return { sort: clicked, dir: current.dir === 'desc' ? 'asc' : 'desc' };
}
